import React from 'react';
import moment from 'moment';
import axios from 'axios';
import Swal from 'sweetalert2';

import { formatPhoneNumber } from 'utils';
import {
  OfferSettingsJsonType,
  AgentGroup,
  PendingRegisteredOffersListing,
  PendingRegisteredOffer,
} from '@bb/domain/dist/types';
import errorReporter from '@app/errorReporter';

import Modal, {
  ModalHeader,
  ModalTitle,
  ModalBody,
  ModalFooter,
} from '../react-common/Modal';
import Loading from '../react-common/Loading';
import PdfPreview, { Offer as PdfPreviewOffer } from './PdfPreview';
import Form801InvalidMessageEditor, {
  Form801RejectionNotification,
} from './Form801InvalidMessageEditor';
import { NewOffersModalListing } from './NewOffersModal';

export interface Offer extends PdfPreviewOffer {
  coop: {
    name: string;
    email: string;
    phone: string;
    org: { name: string; phone: string };
  };
  createdAt: string;
  location?: string;
}

export interface Listing {
  id: string;
  line1: string;
  org: {
    addresses: { [index: number]: { name: string; phone: string }[] };
    locations: { name: string }[];
    name: string;
  };
  location: string;
  address: string;
  reps: {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    frontEndInstructions: { releaseCell: boolean };
  }[];
  isPendingVerification?: boolean;
}

interface Form801ModalProps {
  offer: Offer | PendingRegisteredOffer;
  listing: PendingRegisteredOffersListing;
  show: boolean;
  closeModal: (requireReload?: boolean, verified?: boolean) => void;
}

interface Form801ModalState {
  showInvalidMessageEditor: boolean;
  rejectionNotification: Form801RejectionNotification;
  isLoading: boolean;
  isProcessing: boolean;
  isFormValid: boolean;
  agentGroup: AgentGroup;
}

const emptyBodyErrorMessage = 'Body of the message can not be empty';

class Form801Modal extends React.Component<
  Form801ModalProps,
  Form801ModalState
> {
  constructor(props: Form801ModalProps) {
    super(props);
    const { listing, offer } = props;

    this.state = {
      showInvalidMessageEditor: false,
      rejectionNotification: {
        to: offer.coop.email,
        replyTo: listing.reps[0]?.email,
        subject: `Invalid Offer Doc—${listing.line1}`,
        body: this.generateDefaultRejectionBody(),
      },
      isLoading: true,
      isProcessing: false,
      isFormValid: true,
      agentGroup: 'none',
    };
  }

  public componentDidMount() {
    this.fetchAgentGroup();
  }

  fetchAgentGroup = async () => {
    try {
      const {
        listing: { id: listingId },
      } = this.props;
      if (!listingId) return;

      this.setState({ isLoading: true });

      const {
        data: { agentGroup },
      } = await axios.get<OfferSettingsJsonType>(
        `/listing/${listingId}/offer_settings`,
      );

      this.setState({ agentGroup, isLoading: false });
    } catch (err) {
      errorReporter.error(`Error fetching offer details: ${err}`);
    }
  };

  render() {
    const { offer, listing, show, closeModal } = this.props;
    const {
      showInvalidMessageEditor,
      rejectionNotification,
      isProcessing,
      isFormValid,
      isLoading,
    } = this.state;
    return (
      <Modal
        dialogClassName="form-801-modal"
        show={show}
        onHide={this.handleExit}
      >
        <ModalHeader>
          <ModalTitle data-cy="form-801-modal-title">
            Verify Offer Doc
          </ModalTitle>
          <span data-cy="form-801-modal-address">{listing.line1}</span>
        </ModalHeader>

        <ModalBody data-cy="form-801-modal-body">
          {!isLoading ? (
            !showInvalidMessageEditor ? (
              offer.attachmentForm801 ? (
                <PdfPreview
                  documentPath={offer.attachmentForm801.url}
                  offer={offer}
                />
              ) : (
                <div className="form-801-modal__error">
                  Error: We couldn&apos;t find an offer doc attachment for this
                  offer
                </div>
              )
            ) : (
              <Form801InvalidMessageEditor
                notification={rejectionNotification}
                updateNotification={this.updateRejectionNotification}
                setIsFormValid={this.setIsFormValid}
              />
            )
          ) : (
            <Loading size="fa-5x" />
          )}
        </ModalBody>

        {!showInvalidMessageEditor ? (
          <ModalFooter>
            <div className={`${window.innerWidth > 767 ? '' : 'pull-left'}`}>
              <button
                className="btn btn-default"
                onClick={() => closeModal()}
                disabled={isProcessing || isLoading}
                data-cy="form-801-modal-close"
              >
                Close
              </button>
              <button
                className="btn btn-danger"
                onClick={this.rejectForm}
                disabled={isProcessing || isLoading}
                data-cy="form-801-modal-notify-agent"
              >
                {' Invalid'}
                <span className="hidden-xs">, Notify Showing Agent</span>
              </button>
              <button
                className="btn btn-success"
                onClick={this.acceptForm}
                disabled={isProcessing || isLoading}
                data-cy="form-801-modal-register-offer"
              >
                {' Valid'}
                <span className="hidden-xs">, Register Offer</span>
              </button>
            </div>
          </ModalFooter>
        ) : (
          <ModalFooter>
            <button
              className="btn btn-default"
              onClick={() => closeModal()}
              disabled={isProcessing || isLoading}
              data-cy="form-801-modal-close"
            >
              Cancel
            </button>
            <button
              className="btn btn-danger"
              onClick={this.sendFormRejectionEmail}
              disabled={isProcessing || isLoading || !isFormValid}
              data-cy="form-801-modal-reject"
            >
              Send Rejection
            </button>
          </ModalFooter>
        )}
      </Modal>
    );
  }

  handleExit = () => {
    const { closeModal } = this.props;
    closeModal();
  };

  rejectForm = () => {
    this.setState({ showInvalidMessageEditor: true });
  };

  updateRejectionNotification = (
    rejectionNotification: Form801RejectionNotification,
  ) => {
    this.setState({ rejectionNotification });
  };

  setIsFormValid = (isFormValid: boolean) => {
    this.setState({ isFormValid });
  };

  sendFormRejectionEmail = async () => {
    const { listing, offer, closeModal } = this.props;
    const { rejectionNotification } = this.state;
    const rejectFormUrl = `/listing/${listing.id}/offer/${offer.id}/reject_801`;

    this.setState({ isProcessing: true });

    try {
      if (!rejectionNotification.body) {
        throw new Error(emptyBodyErrorMessage);
      }
      await axios.post(rejectFormUrl, { notification: rejectionNotification });
    } catch (err) {
      if (
        axios.isAxiosError<{ message: string }>(err) &&
        err.response?.data?.message === 'Offer has been deleted'
      ) {
        Swal.fire({
          title: 'Error rejecting form',
          text: 'Could not reject offer doc, the offer has been deleted.',
          type: 'error',
        });
      } else if (err.message === emptyBodyErrorMessage) {
        Swal.fire({
          title: 'Error rejecting form',
          text: `${err.message}. Please try again.`,
          type: 'error',
        });
      } else {
        Swal.fire({
          title: 'Error rejecting form',
          text:
            'Something happened and we could not process the offer doc ' +
            'rejection. Please try again.',
          type: 'error',
        });
      }
    } finally {
      this.setState({ isProcessing: false });
      closeModal(true);
    }
  };

  acceptForm = async () => {
    const { listing, offer, closeModal } = this.props;
    const { agentGroup } = this.state;
    const acceptFormUrl = `/listing/${listing.id}/offer/${offer.id}/verify_801`;
    let shouldNotify = false;

    this.setState({ isProcessing: true });

    try {
      await axios.post(acceptFormUrl);

      // If auto-notify all agents, show success swal for notification
      if (agentGroup === 'all') {
        await Swal.fire({
          type: 'success',
          title: 'Offer Registered',
          text: 'All relevant parties have been notified based on their preferences.',
          confirmButtonColor: '#62cb31',
          confirmButtonText: 'Done',
        });
      }
      shouldNotify = agentGroup !== 'all';
    } catch (err) {
      if (
        axios.isAxiosError<{ message: string }>(err) &&
        err.response?.data?.message === 'Offer has been deleted'
      ) {
        Swal.fire({
          title: 'Error processing form',
          text: 'Could not verify offer doc, the offer has been deleted.',
          type: 'error',
        });
      } else {
        Swal.fire({
          title: 'Error processing form',
          text:
            'Something happened and we could not process the offer doc ' +
            'verification. Please try again.',
          type: 'error',
        });
      }
    } finally {
      this.setState({ isProcessing: false });
      closeModal(true, shouldNotify);
    }
  };

  generateDefaultRejectionBody = () => {
    const { offer, listing } = this.props;
    const formattedDate = `${moment(offer.createdAt).format(
      'MMM Do, YYYY',
    )} at ${moment(offer.createdAt).format('h:mm a')}`;
    const laName = listing.reps?.length
      ? `${listing.reps[0].firstName} ${listing.reps[0].lastName}`
      : '';
    const addressObj =
      listing.location &&
      listing.org.addresses[Number(listing.location)]?.[
        Number(listing.address)
      ];
    const brokerageName = addressObj?.name || listing.org.mls_name;
    const brokeragePhone = addressObj && formatPhoneNumber(addressObj.phone);

    return `Hi ${offer.coop.name},
<br /><br />
This is to inform you that your offer on listing ${
      listing.line1
    } has <b>NOT been registered</b>.  There appear to be errors with your offer doc that was submitted online on ${formattedDate}.
<br />
For more details please contact the listing agent / brokerage.
<br /><br />
${laName}
${brokerageName ? `<br>\n${brokerageName}` : ''}
<br />
Office: ${formatPhoneNumber(brokeragePhone || '')}
${
  listing.reps[0]?.frontEndInstructions?.releaseCell
    ? `<br />
Mobile: ${formatPhoneNumber(listing.reps[0]?.phone || '')}`
    : ''
}
<br />
${listing.reps[0]?.email}`;
  };
}

export default Form801Modal;
